import { useState, type ComponentProps } from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import { handleStepConflict } from '@/lib/stepConflict';
import { Button, cn } from '@ai-tutor/ui';
import { lessonApi, type PipelineStep } from '@/lib/api-client';
import { optimisticallyMarkStepProcessing } from '@/lib/optimisticRetry';

/**
 * Per-panel retry control. Wired to `POST /lessons/{id}/retry/{step}`.
 * The button is intentionally subtle - it sits below the panel's failure
 * state and lets the user re-run just that one step without bumping the
 * lesson version.
 *
 * We optimistically flip the step to `processing` in the pipeline-status cache
 * the instant it's clicked (rolled back if the queue POST fails), so the panel
 * shows "processing" immediately instead of waiting up to one 3s poll. The
 * poller then reconciles with server truth.
 */

interface RetryButtonProps {
  lessonId: string;
  step: PipelineStep;
  /** Visible label - defaults to "Retry". */
  label?: string;
  /** Optional callback fired after the retry is queued (e.g. to refetch). */
  onQueued?: () => void;
  className?: string;
  /** Button variant - defaults to the subtle 'outline' used in panels. */
  variant?: ComponentProps<typeof Button>['variant'];
  /** Button size - defaults to the compact 'sm' used in panels. */
  size?: ComponentProps<typeof Button>['size'];
}

export function RetryButton({
  lessonId,
  step,
  label = 'Retry',
  onQueued,
  className,
  variant = 'outline',
  size = 'sm',
}: RetryButtonProps) {
  const [isQueueing, setIsQueueing] = useState(false);
  const queryClient = useQueryClient();

  const handleClick = async () => {
    setIsQueueing(true);
    // Flip this step to "processing" instantly; rollback restores prior state
    // if the queue POST fails.
    const rollback = optimisticallyMarkStepProcessing(
      queryClient,
      lessonId,
      step
    );
    try {
      await lessonApi.retryStep(lessonId, step);
      notify.success(`${label} queued`);
      onQueued?.();
    } catch (err: unknown) {
      rollback();
      // 409 = nothing to retry (stale screen / double click): refresh calmly.
      if (handleStepConflict(err, queryClient, lessonId)) return;
      // Pass the raw error so notify.error can detect ApiError (401 is
      // silent, network errors use the offline copy, etc.). The fallback
      // only fires when the error has no useful message.
      notify.error(err, "Couldn't queue retry");
    } finally {
      setIsQueueing(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleClick}
      disabled={isQueueing}
      // Keeps its drawn size; tapped as 44px on a phone or touch screen.
      className={cn('tap-target', className)}
    >
      {isQueueing ? (
        <Loader2 className='h-3.5 w-3.5 animate-spin' />
      ) : (
        <RefreshCw className='h-3.5 w-3.5' />
      )}
      {label}
    </Button>
  );
}
